import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DocumentPicker } from './DocumentPicker.js';

type WorkflowAction = 'submit' | 'to-review' | 'approve' | 'reject' | 'publish';

const ACTIONS: ReadonlyArray<{ readonly action: WorkflowAction; readonly labelKey: string }> = [
  { action: 'submit', labelKey: 'submit' },
  { action: 'to-review', labelKey: 'toReview' },
  { action: 'approve', labelKey: 'approve' },
  { action: 'reject', labelKey: 'reject' },
  { action: 'publish', labelKey: 'publish' },
];

export function M23ApprovalScreen(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [state, setState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function loadState(id: string): void {
    setError(null);
    apiRequest<{ data: { state: string } }>(`/v1/document-approvals/${encodeURIComponent(id)}/state`, { token })
      .then((r) => setState(r.data.state))
      .catch((e) => setError((e as Error).message));
  }

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    loadState(id);
  }

  function act(action: WorkflowAction): void {
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/document-approvals/${encodeURIComponent(id)}/${action}`, { method: 'POST', token, body: {} })
      .then(() => { setMessage(t('m23.approval.msgApplied', { action })); loadState(id); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="approval-screen">
      <h1>{t('m23.approval.title')}</h1>
      <form aria-label="approval-load-form" onSubmit={onLoad}>
        <DocumentPicker label={t('m23.approval.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <button type="submit">{t('m23.approval.loadState')}</button>
      </form>
      {state && <p aria-label="workflow-state">{t('m23.approval.currentState')}: {state}</p>}
      <section aria-label="workflow-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {ACTIONS.map((a) => (
          <button key={a.action} type="button" onClick={() => act(a.action)}>{t(`m23.approval.${a.labelKey}`)}</button>
        ))}
      </section>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
